/* eslint-disable @typescript-eslint/no-explicit-any */
import IndeterminateCheckbox from '../components/base/IndeterminateCheckbox';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table';
import { useMemo, memo } from 'react';

const selectionColumn = {
  id: 'select',
  accessorKey: '',
  header: ({ table }) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler()
      }}
    />
  ),
  cell: ({ row }) => (
    <IndeterminateCheckbox
      className="form-check fs-8 mb-0"
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler()
      }}
    />
  ),
  meta: {
    headerProps: { style: { width: '30px' } }
  }
};

const useAdvanceTable = ({
  columns,
  data,
  selection,
  sortable,
  pagination,
  pageSize,
  initialState,
  // Optional. Undefined keeps tanstack's own substring match, so every existing
  // table behaves exactly as it did.
  globalFilterFn,
  // ── server-side paging ──
  // Set these three together when the rows for ONE page are all the browser
  // ever holds. The completed lists only grow — a year of production is tens of
  // thousands of rows — and shipping them all to draw twenty-five is not a page
  // size, it is a download. Left undefined, everything below is inert and the
  // table pages client-side exactly as before.
  manualPagination = false,
  pageCount,
  paginationState,
  onPaginationChange
}) => {
  const state = useMemo(
    () => ({
      pagination: { pageSize: pagination ? pageSize : data.length },
      ...initialState
    }),
    [pagination, pageSize, data.length, initialState, data]
  );

  // Call the hook directly
  const table = useReactTable({
    data,
    columns: selection ? [selectionColumn, ...columns] : columns,
    enableSorting: sortable,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // With manual paging the server has already sliced the page, so slicing it
    // again here would show one row of twenty-five.
    ...(manualPagination
      ? { manualPagination: true, pageCount: pageCount ?? -1 }
      : { getPaginationRowModel: getPaginationRowModel() }),
    ...(paginationState ? { state: { pagination: paginationState } } : {}),
    ...(onPaginationChange ? { onPaginationChange } : {}),
    ...(globalFilterFn ? { globalFilterFn } : {}),
    initialState: state,
    autoResetPageIndex: false
  });

  return table;
};

export default useAdvanceTable;
